import type { ComponentWithAs, IconProps } from "@chakra-ui/react"
import isEqual from "lodash.isequal"
import type { AbiItem } from "web3-utils"

import GenericNetworkIcon from "web3/components/icons/GenericChainIcon"
import chains, { ChainId } from "web3/constants/chains"

export type AddressBookBridgeContract = {
  address: string
  name: string
  chainName: string
  icon?: ComponentWithAs<"svg", IconProps> | null
  abiMethods?: AbiItem[]
}

const getNetworkIcon = (chainId: ChainId) => {
  const chain = chains[chainId]

  const networkIcon = chain ? GenericNetworkIcon(chain) : null

  return networkIcon
}

const ADDRESS_BOOK: Record<
  string,
  Record<string, AddressBookBridgeContract[]>
> = {
  "https://api.tally.xyz": {
    // /gov/compound
    "eip155:1:0x309a862bbC1A00e45506cB8A802D1ff10004c8C0": [
      {
        name: `Bridge to ${chains[ChainId.Optimism].mediumName}`,
        chainName: chains[ChainId.Optimism].mediumName,
        address: "0x25ace71c97B33Cc4729CF772ae268934F7ab5fA1",
        icon: getNetworkIcon(ChainId.Optimism),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_target",
                type: "address",
              },
              {
                internalType: "bytes",
                name: "_message",
                type: "bytes",
              },
              {
                internalType: "uint32",
                name: "_minGasLimit",
                type: "uint32",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.Base].mediumName}`,
        chainName: chains[ChainId.Base].mediumName,
        address: "0x866E82a600A1414e583f7F13623F1aC5d58b0Afa",
        icon: getNetworkIcon(ChainId.Base),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_target",
                type: "address",
              },
              {
                internalType: "bytes",
                name: "_message",
                type: "bytes",
              },
              {
                internalType: "uint32",
                name: "_minGasLimit",
                type: "uint32",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.ArbitrumOne].mediumName}`,
        chainName: chains[ChainId.ArbitrumOne].mediumName,
        address: "0x4Dbd4fc535Ac27206064B68FfCf827b0A60BAB3f",
        icon: getNetworkIcon(ChainId.ArbitrumOne),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "to",
                type: "address",
              },
              {
                internalType: "uint256",
                name: "l2CallValue",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "maxSubmissionCost",
                type: "uint256",
              },
              {
                internalType: "address",
                name: "excessFeeRefundAddress",
                type: "address",
              },
              {
                internalType: "address",
                name: "callValueRefundAddress",
                type: "address",
              },
              {
                internalType: "uint256",
                name: "gasLimit",
                type: "uint256",
              },
              {
                internalType: "uint256",
                name: "maxFeePerGas",
                type: "uint256",
              },
              {
                internalType: "bytes",
                name: "data",
                type: "bytes",
              },
            ],
            name: "createRetryableTicket",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.PolygonMainnet].mediumName}`,
        chainName: chains[ChainId.PolygonMainnet].mediumName,
        address: "0xfe5e5D361b2ad62c541bAb87C45a0B9B018389a2",
        icon: getNetworkIcon(ChainId.PolygonMainnet),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_receiver",
                type: "address",
              },
              {
                internalType: "bytes",
                name: "_data",
                type: "bytes",
              },
            ],
            name: "sendMessageToChild",
            outputs: [],
            stateMutability: "nonpayable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.Scroll].mediumName}`,
        chainName: chains[ChainId.Scroll].mediumName,
        address: "0x6774Bcbd5ceCeF1336b5300fb5186a12DDD8b367",
        icon: getNetworkIcon(ChainId.Scroll),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_to",
                type: "address",
              },
              {
                internalType: "uint256",
                name: "_value",
                type: "uint256",
              },
              {
                internalType: "bytes",
                name: "_message",
                type: "bytes",
              },
              {
                internalType: "uint256",
                name: "_gasLimit",
                type: "uint256",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.Mantle].mediumName}`,
        chainName: chains[ChainId.Mantle].mediumName,
        address: "0x676A795fe6E43C17c668de16730c3F690FEB7120",
        icon: getNetworkIcon(ChainId.Mantle),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_target",
                type: "address",
              },
              {
                internalType: "bytes",
                name: "_message",
                type: "bytes",
              },
              {
                internalType: "uint32",
                name: "_minGasLimit",
                type: "uint32",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.LineaMainnet].mediumName}`,
        chainName: chains[ChainId.LineaMainnet].mediumName,
        address: "0xd19d4B5d358258f05D7B411E21A1460D11B0876F",
        icon: getNetworkIcon(ChainId.LineaMainnet),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_to",
                type: "address",
              },
              {
                internalType: "uint256",
                name: "_fee",
                type: "uint256",
              },
              {
                internalType: "bytes",
                name: "_calldata",
                type: "bytes",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to ${chains[ChainId.Unichain].mediumName}`,
        chainName: chains[ChainId.Unichain].mediumName,
        address: "0x9A3D64E386C18Cb1d6d5179a9596A4B5736e98A6",
        icon: getNetworkIcon(ChainId.Unichain),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_target",
                type: "address",
              },
              {
                internalType: "bytes",
                name: "_message",
                type: "bytes",
              },
              {
                internalType: "uint32",
                name: "_minGasLimit",
                type: "uint32",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
      {
        name: `Bridge to Ronin`,
        chainName: "Ronin",
        address: "0x80226fc0Ee2b096224EeAc085Bb9a8cba1146f7D",
        abiMethods: [
          {
            inputs: [
              {
                internalType: "uint64",
                name: "destinationChainSelector",
                type: "uint64",
              },
              {
                components: [
                  {
                    internalType: "bytes",
                    name: "receiver",
                    type: "bytes",
                  },
                  {
                    internalType: "bytes",
                    name: "data",
                    type: "bytes",
                  },
                  {
                    components: [
                      {
                        internalType: "address",
                        name: "token",
                        type: "address",
                      },
                      {
                        internalType: "uint256",
                        name: "amount",
                        type: "uint256",
                      },
                    ],
                    internalType: "struct Client.EVMTokenAmount[]",
                    name: "tokenAmounts",
                    type: "tuple[]",
                  },
                  {
                    internalType: "address",
                    name: "feeToken",
                    type: "address",
                  },
                  {
                    internalType: "bytes",
                    name: "extraArgs",
                    type: "bytes",
                  },
                ],
                internalType: "struct Client.EVM2AnyMessage",
                name: "message",
                type: "tuple",
              },
            ],
            name: "ccipSend",
            outputs: [
              {
                internalType: "bytes32",
                name: "",
                type: "bytes32",
              },
            ],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
    ],
  },

  "https://api.staging.tally.xyz": {
    // /gov/compound
    "eip155:1:0x309a862bbC1A00e45506cB8A802D1ff10004c8C0": [
      {
        name: `Bridge to ${chains[ChainId.Optimism].mediumName}`,
        chainName: chains[ChainId.Optimism].mediumName,
        address: "0x25ace71c97B33Cc4729CF772ae268934F7ab5fA1",
        icon: getNetworkIcon(ChainId.Optimism),
        abiMethods: [
          {
            inputs: [
              {
                internalType: "address",
                name: "_target",
                type: "address",
              },
              {
                internalType: "bytes",
                name: "_message",
                type: "bytes",
              },
              {
                internalType: "uint32",
                name: "_minGasLimit",
                type: "uint32",
              },
            ],
            name: "sendMessage",
            outputs: [],
            stateMutability: "payable",
            type: "function",
          },
        ],
      },
    ],
  },
}

export const getAddressBookBridgeContracts = (
  govId: string,
): AddressBookBridgeContract[] | undefined => {
  return (
    ADDRESS_BOOK?.[process.env.NEXT_PUBLIC_TALLY_API_URL]?.[govId] ?? undefined
  )
}

export const getAddressBookBridgeContract = (
  govId: string,
  targetAddress: string,
): AddressBookBridgeContract | undefined => {
  const bridgeContracts = getAddressBookBridgeContracts(govId)

  if (bridgeContracts) {
    const bridgeContract = bridgeContracts.find((bridgeContract) =>
      isEqual(
        bridgeContract.address.toLowerCase(),
        targetAddress.toLowerCase(),
      ),
    )

    return bridgeContract
  }

  return undefined
}
